/**
 * Workflow Analytics Service
 * Collects, stores, and analyzes workflow execution data and performance metrics
 */

export interface WorkflowExecution {
  id: string;
  workflowId: string;
  startTime: string;
  endTime: string | null;
  duration: number | null;
  status: 'running' | 'completed' | 'failed' | 'cancelled';
  nodeExecutions: NodeExecution[];
  errorMessage?: string;
  errorType?: string;
  inputData?: any;
  outputData?: any;
  resourceUsage: {
    memoryUsage: number;
    cpuUsage?: number;
    networkRequests?: number;
  };
}

export interface NodeExecution {
  nodeId: string;
  nodeType: string;
  startTime: string;
  endTime: string | null;
  duration: number | null;
  status: 'pending' | 'running' | 'completed' | 'failed' | 'skipped';
  errorMessage?: string;
  inputValues?: Record<string, any>;
  outputValues?: Record<string, any>;
}

export interface WorkflowAnalytics {
  workflowId: string;
  totalExecutions: number;
  successfulExecutions: number;
  failedExecutions: number;
  successRate: number;
  averageExecutionTime: number;
  executionHistory: number[]; // Daily execution counts for last 30 days
  errorTypes: Array<{
    type: string;
    message: string;
    count: number;
    lastOccurrence: string;
  }>;
  nodeExecutionStats: {
    averageNodesPerExecution: number;
    slowestNodeAverage: number;
    fastestNodeAverage: number;
    mostFailedNodes: Array<{
      nodeId: string;
      nodeType: string;
      failureRate: number;
      failureCount: number;
    }>;
  };
  resourceUsage: {
    memoryUsage: number;
    averageMemoryUsage: number;
    peakMemoryUsage: number;
    networkRequests: number;
  };
  firstExecution: string;
  lastExecution: string;
  popularInputs: Array<{
    input: any;
    frequency: number;
    successRate: number;
  }>;
}

export class WorkflowAnalyticsService {
  private static readonly STORAGE_KEY = 'workflow_analytics';
  private static readonly EXECUTIONS_KEY = 'workflow_executions';

  /**
   * Start tracking a workflow execution
   */
  static startExecution(workflowId: string, inputData?: any): string {
    const executionId = `exec_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    
    const execution: WorkflowExecution = {
      id: executionId,
      workflowId,
      startTime: new Date().toISOString(),
      endTime: null,
      duration: null,
      status: 'running',
      nodeExecutions: [],
      inputData,
      resourceUsage: {
        memoryUsage: this.getCurrentMemoryUsage()
      }
    };

    this.saveExecution(execution);
    return executionId;
  }

  /**
   * Record node execution start
   */
  static startNodeExecution(executionId: string, nodeId: string, nodeType: string, inputValues?: Record<string, any>) {
    const execution = this.getExecution(executionId);
    if (!execution) return;

    const nodeExecution: NodeExecution = {
      nodeId,
      nodeType,
      startTime: new Date().toISOString(),
      endTime: null,
      duration: null,
      status: 'running',
      inputValues
    };

    execution.nodeExecutions.push(nodeExecution);
    this.saveExecution(execution);
  }

  /**
   * Complete node execution
   */
  static completeNodeExecution(
    executionId: string, 
    nodeId: string, 
    status: 'completed' | 'failed' | 'skipped',
    outputValues?: Record<string, any>,
    errorMessage?: string
  ) {
    const execution = this.getExecution(executionId);
    if (!execution) return;

    const nodeExecution = execution.nodeExecutions.find(ne => 
      ne.nodeId === nodeId && ne.status === 'running'
    );
    
    if (nodeExecution) {
      nodeExecution.endTime = new Date().toISOString();
      nodeExecution.duration = new Date(nodeExecution.endTime).getTime() - 
                              new Date(nodeExecution.startTime).getTime();
      nodeExecution.status = status;
      nodeExecution.outputValues = outputValues;
      
      if (errorMessage) {
        nodeExecution.errorMessage = errorMessage;
      }

      this.saveExecution(execution);
    }
  }

  /**
   * Complete workflow execution
   */
  static completeExecution(
    executionId: string,
    status: 'completed' | 'failed' | 'cancelled',
    outputData?: any,
    errorMessage?: string,
    errorType?: string
  ) {
    const execution = this.getExecution(executionId);
    if (!execution) return;

    execution.endTime = new Date().toISOString();
    execution.duration = new Date(execution.endTime).getTime() - 
                        new Date(execution.startTime).getTime();
    execution.status = status;
    execution.outputData = outputData;
    execution.resourceUsage.memoryUsage = this.getCurrentMemoryUsage();

    if (errorMessage) {
      execution.errorMessage = errorMessage;
      execution.errorType = errorType || 'Unknown Error';
    }

    this.saveExecution(execution);

    // Update analytics
    this.updateAnalytics(execution);
  }

  /**
   * Get analytics for a specific workflow
   */
  static async getWorkflowAnalytics(workflowId: string): Promise<WorkflowAnalytics> {
    await new Promise(resolve => setTimeout(resolve, 500)); // Simulate async operation

    const executions = this.getExecutionsByWorkflow(workflowId);
    
    if (executions.length === 0) {
      return this.createEmptyAnalytics(workflowId);
    }

    const completedExecutions = executions.filter(e => e.status !== 'running');
    const successfulExecutions = completedExecutions.filter(e => e.status === 'completed');
    const failedExecutions = completedExecutions.filter(e => e.status === 'failed');

    // Calculate execution history (last 30 days)
    const now = new Date();
    const executionHistory = Array.from({ length: 30 }, (_, i) => {
      const date = new Date(now);
      date.setDate(date.getDate() - (29 - i));
      const dayStart = new Date(date.getFullYear(), date.getMonth(), date.getDate());
      const dayEnd = new Date(dayStart);
      dayEnd.setDate(dayEnd.getDate() + 1);

      return completedExecutions.filter(e => {
        const execDate = new Date(e.startTime);
        return execDate >= dayStart && execDate < dayEnd;
      }).length;
    });

    // Analyze error types
    const errorTypeMap = new Map<string, { count: number; lastOccurrence: string; message: string }>();
    failedExecutions.forEach(exec => {
      if (exec.errorType && exec.errorMessage) {
        const existing = errorTypeMap.get(exec.errorType);
        if (existing) {
          existing.count++;
          if (exec.startTime > existing.lastOccurrence) {
            existing.lastOccurrence = exec.startTime;
            existing.message = exec.errorMessage;
          }
        } else {
          errorTypeMap.set(exec.errorType, {
            count: 1,
            lastOccurrence: exec.startTime,
            message: exec.errorMessage
          });
        }
      }
    });

    // Node execution statistics
    const allNodeExecutions = completedExecutions.flatMap(e => e.nodeExecutions);
    const nodeFailureMap = new Map<string, { failures: number; total: number; type: string }>();
    
    allNodeExecutions.forEach(ne => {
      const key = `${ne.nodeId}_${ne.nodeType}`;
      const existing = nodeFailureMap.get(key) || { failures: 0, total: 0, type: ne.nodeType };
      existing.total++;
      if (ne.status === 'failed') existing.failures++;
      nodeFailureMap.set(key, existing);
    });

    const mostFailedNodes = Array.from(nodeFailureMap.entries())
      .map(([nodeId, stats]) => ({
        nodeId: nodeId.split('_')[0],
        nodeType: stats.type,
        failureRate: stats.failures / stats.total,
        failureCount: stats.failures
      }))
      .filter(node => node.failureCount > 0)
      .sort((a, b) => b.failureRate - a.failureRate)
      .slice(0, 5);

    // Resource usage analysis
    const memoryUsages = completedExecutions.map(e => e.resourceUsage.memoryUsage).filter(Boolean);
    const networkRequests = completedExecutions.reduce((sum, e) => 
      sum + (e.resourceUsage.networkRequests || 0), 0
    );

    // Popular inputs analysis
    const inputMap = new Map<string, { frequency: number; successes: number; total: number }>();
    completedExecutions.forEach(exec => {
      if (exec.inputData) {
        const inputKey = JSON.stringify(exec.inputData);
        const existing = inputMap.get(inputKey) || { frequency: 0, successes: 0, total: 0 };
        existing.frequency++;
        existing.total++;
        if (exec.status === 'completed') existing.successes++;
        inputMap.set(inputKey, existing);
      }
    });

    const popularInputs = Array.from(inputMap.entries())
      .map(([input, stats]) => ({
        input: JSON.parse(input),
        frequency: stats.frequency,
        successRate: stats.successes / stats.total
      }))
      .sort((a, b) => b.frequency - a.frequency)
      .slice(0, 10);

    return {
      workflowId,
      totalExecutions: completedExecutions.length,
      successfulExecutions: successfulExecutions.length,
      failedExecutions: failedExecutions.length,
      successRate: completedExecutions.length > 0 ? successfulExecutions.length / completedExecutions.length : 0,
      averageExecutionTime: successfulExecutions.length > 0 
        ? Math.round(successfulExecutions.reduce((sum, e) => sum + (e.duration || 0), 0) / successfulExecutions.length)
        : 0,
      executionHistory,
      errorTypes: Array.from(errorTypeMap.entries()).map(([type, data]) => ({
        type,
        message: data.message,
        count: data.count,
        lastOccurrence: data.lastOccurrence
      })),
      nodeExecutionStats: {
        averageNodesPerExecution: completedExecutions.length > 0 
          ? Math.round(allNodeExecutions.length / completedExecutions.length)
          : 0,
        slowestNodeAverage: allNodeExecutions.length > 0
          ? Math.max(...allNodeExecutions.map(ne => ne.duration || 0))
          : 0,
        fastestNodeAverage: allNodeExecutions.length > 0
          ? Math.min(...allNodeExecutions.filter(ne => ne.duration).map(ne => ne.duration!))
          : 0,
        mostFailedNodes
      },
      resourceUsage: {
        memoryUsage: memoryUsages.length > 0 ? memoryUsages[memoryUsages.length - 1] : 0,
        averageMemoryUsage: memoryUsages.length > 0 
          ? memoryUsages.reduce((sum, mem) => sum + mem, 0) / memoryUsages.length
          : 0,
        peakMemoryUsage: memoryUsages.length > 0 ? Math.max(...memoryUsages) : 0,
        networkRequests
      },
      firstExecution: executions.length > 0 
        ? executions.sort((a, b) => new Date(a.startTime).getTime() - new Date(b.startTime).getTime())[0].startTime
        : new Date().toISOString(),
      lastExecution: executions.length > 0 
        ? executions.sort((a, b) => new Date(b.startTime).getTime() - new Date(a.startTime).getTime())[0].startTime
        : new Date().toISOString(),
      popularInputs
    };
  }

  /**
   * Get all executions for a workflow
   */
  static getExecutionsByWorkflow(workflowId: string): WorkflowExecution[] {
    const allExecutions = this.getAllExecutions();
    return allExecutions.filter(e => e.workflowId === workflowId);
  }

  /**
   * Get execution by ID
   */
  static getExecution(executionId: string): WorkflowExecution | null {
    const allExecutions = this.getAllExecutions();
    return allExecutions.find(e => e.id === executionId) || null;
  }

  /**
   * Clear analytics data for a workflow
   */
  static clearWorkflowAnalytics(workflowId: string) {
    // Remove executions
    const allExecutions = this.getAllExecutions();
    const filteredExecutions = allExecutions.filter(e => e.workflowId !== workflowId);
    localStorage.setItem(this.EXECUTIONS_KEY, JSON.stringify(filteredExecutions));

    // Remove analytics cache
    const analytics = this.getStoredAnalytics();
    delete analytics[workflowId];
    localStorage.setItem(this.STORAGE_KEY, JSON.stringify(analytics));
  }

  /**
   * Get aggregate analytics for all workflows
   */
  static getOverallAnalytics(): {
    totalWorkflows: number;
    totalExecutions: number;
    averageSuccessRate: number;
    mostActiveWorkflows: Array<{ workflowId: string; executions: number }>;
  } {
    const allExecutions = this.getAllExecutions();
    const workflowStats = new Map<string, { executions: number; successes: number }>();

    allExecutions.forEach(exec => {
      if (exec.status !== 'running') {
        const stats = workflowStats.get(exec.workflowId) || { executions: 0, successes: 0 };
        stats.executions++;
        if (exec.status === 'completed') stats.successes++;
        workflowStats.set(exec.workflowId, stats);
      }
    });

    const mostActiveWorkflows = Array.from(workflowStats.entries())
      .map(([workflowId, stats]) => ({ workflowId, executions: stats.executions }))
      .sort((a, b) => b.executions - a.executions)
      .slice(0, 10);

    const totalSuccesses = Array.from(workflowStats.values()).reduce((sum, stats) => sum + stats.successes, 0);
    const totalExecutions = Array.from(workflowStats.values()).reduce((sum, stats) => sum + stats.executions, 0);

    return {
      totalWorkflows: workflowStats.size,
      totalExecutions,
      averageSuccessRate: totalExecutions > 0 ? totalSuccesses / totalExecutions : 0,
      mostActiveWorkflows
    };
  }

  // Private helper methods

  private static saveExecution(execution: WorkflowExecution) {
    const allExecutions = this.getAllExecutions();
    const index = allExecutions.findIndex(e => e.id === execution.id);
    
    if (index >= 0) {
      allExecutions[index] = execution;
    } else {
      allExecutions.push(execution);
    }

    localStorage.setItem(this.EXECUTIONS_KEY, JSON.stringify(allExecutions));
  }

  private static getAllExecutions(): WorkflowExecution[] {
    try {
      const stored = localStorage.getItem(this.EXECUTIONS_KEY);
      return stored ? JSON.parse(stored) : [];
    } catch (error) {
      console.error('Error loading executions:', error);
      return [];
    }
  }

  private static updateAnalytics(execution: WorkflowExecution) {
    // This could trigger additional analytics processing
    // For now, we rely on the getWorkflowAnalytics method to calculate on demand
  }

  private static getStoredAnalytics(): Record<string, any> {
    try {
      const stored = localStorage.getItem(this.STORAGE_KEY);
      return stored ? JSON.parse(stored) : {};
    } catch (error) {
      console.error('Error loading analytics:', error);
      return {};
    }
  }

  private static createEmptyAnalytics(workflowId: string): WorkflowAnalytics {
    return {
      workflowId,
      totalExecutions: 0,
      successfulExecutions: 0,
      failedExecutions: 0,
      successRate: 0,
      averageExecutionTime: 0,
      executionHistory: Array(30).fill(0),
      errorTypes: [],
      nodeExecutionStats: {
        averageNodesPerExecution: 0,
        slowestNodeAverage: 0,
        fastestNodeAverage: 0,
        mostFailedNodes: []
      },
      resourceUsage: {
        memoryUsage: 0,
        averageMemoryUsage: 0,
        peakMemoryUsage: 0,
        networkRequests: 0
      },
      firstExecution: new Date().toISOString(),
      lastExecution: new Date().toISOString(),
      popularInputs: []
    };
  }

  private static getCurrentMemoryUsage(): number {
    // Estimate memory usage (in MB)
    if ('memory' in performance) {
      return Math.round((performance as any).memory.usedJSHeapSize / 1024 / 1024);
    }
    return Math.round(Math.random() * 50 + 10); // Fallback estimate
  }
}

export const workflowAnalyticsService = new WorkflowAnalyticsService();